import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * 화면 <b>가운데</b>에 잠깐 뜨는 알림.
 *
 * <p>2026-09-02 담당자 요청입니다 — <b>"저장을 클릭하면 저장됐다는 내용이 상단에
 * 표시되다 보니 저장됐는지 알기가 어려워"</b>. 긴 화면에서는 저장 단추가 <b>맨 아래</b>에
 * 있고 알림은 <b>맨 위</b>에 떠서, 눌러도 아무 일이 없는 것처럼 보였습니다.</p>
 *
 * <p>화면마다 쓰던 알림 줄은 <b>그대로 둡니다.</b> 자리를 옮기는 것이 아니라
 * <b>가운데에도 한 번 더</b> 보여 주는 것입니다 — 스크롤을 올리면 그 줄이 그대로 있어,
 * 무엇이 저장됐는지 다시 확인할 수 있습니다.</p>
 */
export type ToastKind = 'ok' | 'err'

interface Toast {
  message: string
  kind: ToastKind
}

/** 알림이 떠 있는 시간. 짧으면 못 보고, 길면 다음 일을 가립니다. */
const OK_MS = 1_800
const ERR_MS = 3_500

export const useToastStore = defineStore('toast', () => {
  const current = ref<Toast | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null

  /**
   * 알림을 띄웁니다. 이미 떠 있으면 <b>새 것으로 바꿉니다</b> —
   * 줄줄이 쌓아 두면 마지막에 무슨 일이 있었는지 알기 어렵습니다.
   *
   * <p>같은 저장에 대해 통로가 먼저 일반적인 말("저장했습니다")을 띄우고
   * 화면이 곧바로 더 자세한 말로 바꿔 다는 식으로 씁니다.</p>
   */
  function show(message: string, kind: ToastKind = 'ok'): void {
    if (!message) return

    // 앞의 알림이 걸어 둔 시계를 <b>먼저 끕니다.</b> 안 끄면 그것이 나중에 깨어나
    // <b>새로 띄운 알림을 지웁니다</b> — 새 알림이 제 시간을 못 채웁니다.
    if (timer) clearTimeout(timer)

    current.value = { message, kind }
    timer = setTimeout(() => {
      current.value = null
      timer = null
    }, kind === 'err' ? ERR_MS : OK_MS)
  }

  /** 손으로 닫기. 눌러서 지울 수 있어야 급할 때 가리지 않습니다. */
  function dismiss(): void {
    if (timer) clearTimeout(timer)
    timer = null
    current.value = null
  }

  return { current, show, dismiss }
})
